import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  FiCode,
  FiLayers,
  FiZap,
  FiCheck,
  FiArrowRight,
  FiAward,
  FiUsers,
  FiTrendingUp
} from 'react-icons/fi';
import SEO from '../components/SEO';
import './About.css';

const AnimatedSection = ({ children, className, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const About = () => {
  const skills = [
    { name: 'React / Next.js', level: 100 },
    { name: 'Node.js / Express', level: 100 },
    { name: 'MongoDB / PostgreSQL', level: 100 },
    { name: 'UX/UI Design', level: 100 },
    { name: 'SEO Optimization', level: 100 },
    { name: 'API Integrations', level: 100 }
  ];

  const expertise = [
    'Financial platforms with Plaid & Stripe',
    'HIPAA-compliant healthcare apps',
    'AI-powered automotive tools',
    'Real-time booking systems',
    'E-commerce marketplaces',
    'React Native mobile apps'
  ];

  const stats = [
    { icon: <FiTrendingUp />, value: '12+', label: 'Years Experience' },
    { icon: <FiLayers />, value: '500+', label: 'Projects Delivered' },
    { icon: <FiUsers />, value: '100%', label: 'Client Satisfaction' },
    { icon: <FiAward />, value: '4.33', label: 'GPA at UT Austin' }
  ];

  return (
    <>
      <SEO
        title="About"
        description="Meet James McEwen, founder of SilverFox Media: a Palantir-certified application developer with 10+ years in software, UX and web development, building products, internal tools and data platforms."
        url="/about"
      />

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero__bg">
          <div className="about-hero__gradient-orb about-hero__gradient-orb--1"></div>
          <div className="about-hero__gradient-orb about-hero__gradient-orb--2"></div>
          <div className="about-hero__grid-overlay"></div>
        </div>
        <div className="container">
          <div className="about-hero__grid">
            <motion.div
              className="about-hero__content"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="about-hero__label">About SilverFox Media</span>
              <h1 className="about-hero__title">
                Meet James — <span className="text-gradient">Developer, Designer, Problem Solver</span>
              </h1>
              <p className="about-hero__subtitle">
                Based in Allen, Texas, I've spent over 12 years helping businesses establish their digital presence through thoughtful UX design, high-performance web development, and strategic SEO.
              </p>
            </motion.div>
            <motion.div
              className="about-hero__image"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img src="/images/james-mcewan.jpg" alt="James McEwan - Founder of SilverFox Media" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="about-stats">
        <div className="container">
          <div className="about-stats__grid">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="about-stats__item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <span className="about-stats__icon">{stat.icon}</span>
                <span className="about-stats__value">{stat.value}</span>
                <span className="about-stats__label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="about-content section">
        <div className="container">
          <div className="about-content__grid">
            {/* Left Column - Story */}
            <div className="about-content__main">
              <AnimatedSection>
                <div className="about-block">
                  <h2 className="about-block__title">Education & Expertise</h2>
                  <p>
                    I'm a graduate of the <strong>UT Austin McCombs Full Stack MERN Development program</strong>, where I earned a 4.33 GPA while mastering the modern JavaScript ecosystem. My technical foundation spans React, Node.js, Express, and MongoDB, allowing me to build custom web applications from the ground up.
                  </p>
                  <p>
                    But technology is only half the equation. With over a decade of UX design experience, I understand that great digital products aren't just built—they're designed with intention. Every layout, interaction, and line of code serves a purpose: to connect your business with the people who need it.
                  </p>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div className="about-block">
                  <h2 className="about-block__title">What I Build</h2>
                  <p>
                    My portfolio spans industries and complexity levels. I've developed financial platforms with Plaid and Stripe integrations, HIPAA-compliant healthcare applications, AI-powered automotive tools, real-time booking systems, e-commerce marketplaces, and mobile apps with React Native.
                  </p>
                  <p>
                    Whether your project requires payment processing, secure data handling, third-party API integrations, or machine learning capabilities, I have the experience to deliver.
                  </p>
                  <div className="about-expertise">
                    {expertise.map((item, index) => (
                      <div key={index} className="about-expertise__item">
                        <FiCheck className="about-expertise__icon" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="about-block">
                  <h2 className="about-block__title">Premium Templates</h2>
                  <p>
                    I also offer premium website templates for specific industries—multifamily apartments, law firms, real estate agencies, and commercial properties—designed to get businesses online quickly with professional, customizable solutions that require no coding.
                  </p>
                  <Link to="/templates" className="about-block__link">
                    Browse Templates <FiArrowRight />
                  </Link>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div className="about-block about-block--highlight">
                  <div className="about-block__icon">
                    <FiZap />
                  </div>
                  <h2 className="about-block__title">How I Work</h2>
                  <p>
                    I leverage AI-powered development workflows to deliver projects faster without sacrificing quality. This means you get a modern, hand-crafted solution at a pace that keeps your business moving forward.
                  </p>
                </div>
              </AnimatedSection>
            </div>

            {/* Right Column - Skills & CTA */}
            <div className="about-content__sidebar">
              <AnimatedSection delay={0.2}>
                <div className="about-skills">
                  <h3 className="about-skills__title">Technical Skills</h3>
                  <div className="about-skills__list">
                    {skills.map((skill, index) => (
                      <div key={skill.name} className="about-skill">
                        <div className="about-skill__header">
                          <span className="about-skill__name">{skill.name}</span>
                          <span className="about-skill__level">{skill.level}%</span>
                        </div>
                        <div className="about-skill__bar">
                          <motion.div
                            className="about-skill__progress"
                            initial={{ width: 0 }}
                            whileInView={{ width: `${skill.level}%` }}
                            transition={{ duration: 1, delay: index * 0.1 }}
                            viewport={{ once: true }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.3}>
                <div className="about-cta-card">
                  <h3>Let's Build Something Together</h3>
                  <p>Your website is often the first impression customers have of your business. Let's make it count.</p>
                  <Link to="/contact" className="btn-glow">
                    Start a Project
                    <FiArrowRight />
                  </Link>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.4}>
                <div className="about-contact-card">
                  <h4>Quick Contact</h4>
                  <a href="tel:9728008105" className="about-contact-card__link">
                    (972) 800-8105
                  </a>
                  <a href="mailto:information@silverfoxmedia.co" className="about-contact-card__link">
                    information@silverfoxmedia.co
                  </a>
                  <span className="about-contact-card__location">
                    Allen, Texas
                  </span>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-bottom-cta">
        <div className="about-bottom-cta__bg">
          <div className="about-bottom-cta__gradient"></div>
        </div>
        <div className="container">
          <AnimatedSection className="about-bottom-cta__content">
            <h2>Ready to Get Started?</h2>
            <p>Let's discuss your project and bring your vision to life.</p>
            <div className="about-bottom-cta__buttons">
              <Link to="/contact" className="btn-glow btn-glow--large">
                Get a Free Quote
                <FiArrowRight />
              </Link>
              <Link to="/portfolio" className="btn-outline-dark">
                View My Work
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default About;
